// Rock mutations, independent of which workspace is active (#238).
import { applyWrite } from '@/shared/lib/store-write';
import { resolveActor } from '@/modules/audit/audit-log';
import type { Rock, RockUpdate } from '@/modules/rocks/types';

import type { MutationContext } from '@/modules/activity/mutation-context';

/** The database shape of a rock patch. Only the fields the caller actually set. */
export function rockDbUpdates(updates: Partial<Rock>): Record<string, unknown> {
  const db: Record<string, unknown> = {};
  if (updates.title !== undefined) db.title = updates.title;
  if (updates.description !== undefined) db.description = updates.description;
  // `in` rather than `!== undefined`: clearing the parent goal means passing an
  // explicit `goalId: undefined`, which a value check would silently drop — the
  // local spread would clear it while the row kept its old goal, so it came back
  // on reload.
  if ('goalId' in updates) db.goal_id = updates.goalId ?? null;
  if (updates.status !== undefined) db.status = updates.status;
  if ('ownerId' in updates) db.owner_id = updates.ownerId || null;
  if (updates.quarter !== undefined) db.quarter = updates.quarter;
  if (updates.year !== undefined) db.year = updates.year;
  if (updates.dueDate !== undefined) db.due_date = updates.dueDate;
  if (updates.plannedWeight !== undefined) db.planned_weight = updates.plannedWeight ?? null;
  return db;
}

export function addRock(
  ctx: MutationContext,
  rock: Rock,
  hooks: { apply(rock: Rock): void; rollback(): void },
): Promise<boolean> {
  if (ctx.isBlocked(rock.year, rock.quarter)) return Promise.resolve(false);
  const ownerId = ctx.resolveOwnerId(rock.ownerId);
  const final: Rock = {
    ...rock,
    teamId: rock.teamId ?? ctx.defaultTeamId(),
    ownerId: ownerId ?? rock.ownerId,
    createdAt: rock.createdAt ?? new Date().toISOString(),
  };
  hooks.apply(final);
  return applyWrite(ctx.api('/records/rocks', {
    ...(final.teamId ? { team_id: final.teamId } : {}),
    id: final.id, goal_id: final.goalId ?? null, title: final.title, status: final.status,
    owner_id: ownerId ?? null, quarter: final.quarter, year: final.year,
    due_date: final.dueDate ?? null,
    source_rock_id: final.sourceRockId ?? null,
    planned_weight: final.plannedWeight ?? null,
    description: final.description ?? null,
    workspace_id: ctx.workspaceId,
    unlocked_cycle: ctx.unlockedCycleKey?.() ?? null,
  }), {
    noun: 'this rock',
    context: 'addRock',
    rollback: hooks.rollback,
  });
}

export function updateRock(
  ctx: MutationContext,
  id: string,
  updates: Partial<Rock>,
  before: Rock | undefined,
  hooks: {
    apply(updates: Partial<Rock>): void;
    rollback(): void;
    recordStatusChange?(status: Rock['status'], updateId: string): void;
  },
): Promise<boolean> {
  // An entity's writability follows its OWN cycle, not the displayed one.
  if (before && ctx.isBlocked(before.year, before.quarter)) return Promise.resolve(false);
  hooks.apply(updates);

  const db = rockDbUpdates(updates);
  if (!Object.keys(db).length) return Promise.resolve(true);

  const status = updates.status;
  const changesStatus = status !== undefined && !!before && status !== before.status;
  const statusUpdateId = changesStatus && hooks.recordStatusChange ? crypto.randomUUID() : undefined;

  return applyWrite(ctx.api(`/records/rocks/${id}`, {
    ...db,
    ...(statusUpdateId ? { status_update_id: statusUpdateId } : { skip_status_record: true }),
    unlocked_cycle: ctx.unlockedCycleKey?.() ?? null,
  }, 'PATCH'), {
    noun: 'this rock',
    context: 'updateRock',
    rollback: hooks.rollback,
    onSuccess: () => {
      if (status !== undefined && statusUpdateId) hooks.recordStatusChange?.(status, statusUpdateId);
    },
  });
}

export function deleteRock(
  ctx: MutationContext,
  id: string,
  before: Rock | undefined,
  hooks: { apply(): void },
): Promise<boolean> {
  if (before && ctx.isBlocked(before.year, before.quarter)) return Promise.resolve(false);
  hooks.apply();
  return applyWrite(ctx.api(`/records/rocks/${id}`, { unlocked_cycle: ctx.unlockedCycleKey?.() ?? null }, 'DELETE'), {
    noun: 'this rock',
    context: 'deleteRock',
    operation: 'delete',
    refetch: ctx.refetch,
  });
}

export interface RockUpdateInput {
  rockId: string;
  message?: string;
  statusAtUpdate: Rock['status'];
  isSystemGenerated?: boolean;
}

export async function addRockUpdate(
  ctx: MutationContext,
  input: RockUpdateInput,
  hooks: {
    apply(update: RockUpdate): void;
    rollback(): void;
    /** The rock being updated, for its title and current status. */
    rock?: Pick<Rock, 'id' | 'title' | 'status'>;
    /** Bring the rock's own status in line with the update just posted. */
    setStatus(status: Rock['status']): void;
  },
): Promise<void> {
  const { rockId, message, statusAtUpdate, isSystemGenerated } = input;
  const workspaceId = ctx.workspaceId;
  if (!workspaceId) return;
  const actor = await resolveActor(ctx.audit);
  if (!actor) return;

  const update: RockUpdate = {
    id: crypto.randomUUID(),
    workspaceId,
    rockId,
    authorId: actor.userId,
    authorMemberId: actor.memberId ?? undefined,
    message: message || undefined,
    statusAtUpdate,
    isSystemGenerated: isSystemGenerated ?? false,
    createdAt: new Date().toISOString(),
  };
  hooks.apply(update);

  applyWrite(ctx.api('/records/rock-updates', {
    id: update.id, workspace_id: workspaceId, rock_id: rockId,
    message: message || null,
    status_at_update: statusAtUpdate,
    is_system_generated: isSystemGenerated ?? false,
  }), {
    noun: 'this update',
    context: 'addRockUpdate',
    rollback: hooks.rollback,
    onSuccess: () => {
      if (isSystemGenerated) return;
      if (hooks.rock && hooks.rock.status !== statusAtUpdate) hooks.setStatus(statusAtUpdate);
    },
  });
}
